import type { AppRouterContext } from './routerAccess';

/**
 * Route loaders that preload a page's first query (CR-19). Each imports the page's query module on
 * demand, so the document stays in the page chunk; the guard lives in `preloadRouteQuery`.
 */

/** The Sales Order route opens on its core. */
export async function loadSalesOrderCore(context: AppRouterContext, slug: string) {
  const { preloadSalesOrderCore } = await import('src/modules/orders/salesOrders/hydration/salesOrderEagerQueries');
  return { coreRef: preloadSalesOrderCore(context, slug) };
}

/** The Pick Ticket route opens on its own core, which selects what the Sales Order core does. */
export async function loadPickTicketCore(context: AppRouterContext, slug: string) {
  const { preloadPickTicketCore } = await import('src/modules/inventory/pickTickets/detail/pickTicketQueries');
  return { coreRef: preloadPickTicketCore(context, slug) };
}

/** The member Purchase Order page's core; the customer portal PO page runs its own queries. */
export async function loadPurchaseOrderCore(context: AppRouterContext, slug: string) {
  const { preloadPurchaseOrderCore } = await import('src/modules/orders/purchaseOrders/detail/purchaseOrderSections');
  return { coreRef: preloadPurchaseOrderCore(context, slug) };
}

export async function loadGrowerProductCore(context: AppRouterContext, slug: string) {
  const { preloadGrowerProductCore } = await import('src/modules/orders/growerProducts/detail/growerProductSections');
  return { coreRef: preloadGrowerProductCore(context, slug) };
}

export async function loadBuySellOrderCore(context: AppRouterContext, slug: string) {
  const { preloadBuySellOrderCore } = await import('src/modules/orders/buySellOrders/detail/buySellOrderSections');
  return { coreRef: preloadBuySellOrderCore(context, slug) };
}

export async function loadDocumentOnboardingCore(context: AppRouterContext, id: number) {
  const { preloadDocumentOnboardingCore } = await import('src/modules/documentOnboardings');
  return { coreRef: preloadDocumentOnboardingCore(context, { id }) };
}

export async function loadProductionRunCore(context: AppRouterContext, slug: string) {
  const { preloadProductionRunCore } = await import('src/modules/inventory/productionRuns/detail/productionRunQueries');
  return { coreRef: preloadProductionRunCore(context, slug) };
}

export async function loadInventoryTransferCore(context: AppRouterContext, slug: string) {
  const { preloadInventoryTransferCore } = await import(
    'src/modules/inventory/inventoryTransfers/detail/inventoryTransferSections'
  );
  return { coreRef: preloadInventoryTransferCore(context, slug) };
}

export async function loadLotCore(context: AppRouterContext, slug: string, warehouseId: number) {
  const { preloadLotCore } = await import('src/modules/lots/hooks/useLotSections');
  return { coreRef: preloadLotCore(context, { slug, warehouseId }) };
}

/** One route, two cores: a `GP-` ticket is keyed by the grower product's bare slug (EditReceivingTicketPage). */
export async function loadReceivingTicketCore(context: AppRouterContext, slug: string) {
  const [orderType, bareSlug] = slug.split('-');
  if (orderType.toLocaleUpperCase() === 'GP') {
    const { preloadGrowerProductReceivingTicketCore } = await import(
      'src/modules/orders/growerProducts/detail/receiving/growerProductReceivingTicketQueries'
    );
    return { coreRef: preloadGrowerProductReceivingTicketCore(context, bareSlug) };
  }
  const { preloadReceivingTicketCore } = await import(
    'src/modules/orders/purchaseOrders/detail/receiving/receivingTicketSections'
  );
  return { coreRef: preloadReceivingTicketCore(context, slug) };
}

/** The Sales Orders list's first grid page, with the filters the grid persisted. */
export async function loadSalesOrdersFirstPage(context: AppRouterContext) {
  const { preloadSalesOrdersGrid } = await import('src/modules/orders/salesOrders/list/api/grid');
  return { listRef: preloadSalesOrdersGrid(context) };
}

/** The member Purchase Orders list's first grid page; the customer portal list runs its own read. */
export async function loadPurchaseOrdersFirstPage(context: AppRouterContext) {
  const { preloadPurchaseOrdersGrid } = await import('src/modules/orders/purchaseOrders/api/grid');
  return { listRef: preloadPurchaseOrdersGrid(context) };
}

export async function loadGrowerProductsFirstPage(context: AppRouterContext) {
  const { preloadGrowerProductsGrid } = await import('src/modules/orders/growerProducts/api/grid');
  return { listRef: preloadGrowerProductsGrid(context) };
}

export async function loadProductionRunsFirstPage(context: AppRouterContext) {
  const { preloadProductionRunsGrid } = await import('src/modules/inventory/productionRuns/useProductionRunsPage');
  return { listRef: preloadProductionRunsGrid(context) };
}

export async function loadPickTicketsFirstPage(context: AppRouterContext) {
  const { preloadPickTicketsGrid } = await import('src/modules/inventory/pickTickets/usePickTicketsPage');
  return { listRef: preloadPickTicketsGrid(context) };
}

export async function loadReceivingTicketsFirstPage(context: AppRouterContext) {
  const { preloadReceivingTicketsGrid } = await import(
    'src/modules/inventory/receivingTickets/useReceivingTicketsPage'
  );
  return { listRef: preloadReceivingTicketsGrid(context) };
}

export async function loadInventoryTransfersFirstPage(context: AppRouterContext) {
  const { preloadInventoryTransfersGrid } = await import(
    'src/modules/inventory/inventoryTransfers/useInventoryTransfersPage'
  );
  return { listRef: preloadInventoryTransfersGrid(context) };
}

/** The Inventory page's warehouse read; its forecast grid loads through its own data source. */
export async function loadInventoryWarehouses(context: AppRouterContext) {
  const { preloadInventoryWarehouses } = await import('src/pages/InventoryPage/InventoryPage');
  return { warehousesRef: preloadInventoryWarehouses(context) };
}

/** The AI inbox's first thread list page, from its persisted view and filters. */
export async function loadAiInboxThreads(context: AppRouterContext) {
  const { preloadInboxThreads } = await import('src/modules/ai-inbox/hooks/useInboxThreads');
  return { threadsRef: preloadInboxThreads(context) };
}

export async function loadBusinessEntity(context: AppRouterContext, id: number) {
  const { preloadBusinessEntity } = await import(
    'src/modules/settings/business/businessEntity/hooks/useBusinessEntity'
  );
  return { entityRef: preloadBusinessEntity(context, id) };
}

export async function loadPricingSheet(context: AppRouterContext, id: number) {
  const { preloadPricingSheet } = await import(
    'src/modules/settings/business/pricingSheets/containers/EditPricingSheet'
  );
  return { pricingSheetRef: preloadPricingSheet(context, id) };
}

/** Business Setup's open tab, when that tab's grid reads a loader ref. */
export async function loadBusinessSetupTab(context: AppRouterContext, tab: string | undefined) {
  const { preloadBusinessSetupTab } = await import('src/modules/settings/business/BusinessSettingsPage');
  return preloadBusinessSetupTab(context, tab);
}

/** Inventory Settings' shown tab: the catalog's first commodity window or the warehouse list. */
export async function loadInventorySettingsTab(context: AppRouterContext, tab: string | undefined) {
  const { preloadInventorySettingsTab } = await import('src/modules/settings/inventory/InventorySettingsPage');
  return preloadInventorySettingsTab(context, tab);
}

/** Warehouse details: the warehouse header read; the locations grid reads beside it. */
export async function loadWarehouseDetails(context: AppRouterContext, warehouseId: number) {
  const { preloadWarehouse } = await import('src/modules/settings/inventory/warehouses/page/useWarehouseSections');
  return { warehouseRef: preloadWarehouse(context, warehouseId) };
}

/** The Team page's email-forwarding banner; People reads Clerk, not Apollo. */
export async function loadTeamForwardingBanner(context: AppRouterContext) {
  const { preloadAiForwardingConfirmation } = await import('src/modules/settings/team/AiForwardingConfirmationBanner');
  return { forwardingRef: preloadAiForwardingConfirmation(context) };
}

/** `/internal/pallet-tags`: the tag layout list. */
export async function loadTagLayouts(context: AppRouterContext) {
  const { preloadTagLayouts } = await import('src/modules/tagLayouts');
  return { listRef: preloadTagLayouts(context) };
}

/** The Order Board's enabled light card queries, from its persisted filters (none before a warehouse is chosen). */
export async function loadOrderBoardCards(context: AppRouterContext) {
  const { preloadOrderBoardCards } = await import('src/modules/orderBoard');
  return preloadOrderBoardCards(context);
}
